import { ref, onUnmounted } from 'vue'
import * as Sentry from '@sentry/vue'

import { useChatMessageMutation, useChatFeedbackMutation } from '@/components/chatbot/queries'
import { useChatContext } from '@/components/chatbot/useChatContext'

export const authors = {
  USER: 'USER',
  BOT: 'BOT',
  FEEDBACK: 'FEEDBACK',
}

let lastMessageId = 0
const isChatOpen = ref(false)
const conversationId = ref(null)
const chatMessages = ref([])
const isAwaitingReply = ref(false)
const isIdle = ref(false)
const idleTimeMs = 3 * 60 * 1000 // 3 minutes
let idleTimer = null

function saveChatInStorage() {
  try {
    const data = chatMessages.value.map(msg => ({
      ...msg,
      timestamp: msg.timestamp.getTime(),
    }))

    sessionStorage.setItem('conversationId', conversationId.value)
    sessionStorage.setItem('chatSession', JSON.stringify(data))
  } catch (error) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'chatbot')
      Sentry.captureException(error)
    })
  }
}

function loadChatFromStorage() {
  try {
    const storedChatMessages = sessionStorage.getItem('chatSession')
    const storedConversationId = sessionStorage.getItem('conversationId')
    if (!storedChatMessages || !storedConversationId) {
      sessionStorage.removeItem('chatSession')
      sessionStorage.removeItem('conversationId')
      return
    }
    const parsedStoredChatMessages = JSON.parse(storedChatMessages)?.map(msg => ({
      ...msg,
      timestamp: new Date(msg.timestamp),
    }))
    chatMessages.value = parsedStoredChatMessages
    conversationId.value = storedConversationId
    lastMessageId = parsedStoredChatMessages[parsedStoredChatMessages.length - 1]?.id
  } catch (error) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'chatbot')
      Sentry.captureException(error)
    })
    sessionStorage.removeItem('chatSession')
    sessionStorage.removeItem('conversationId')
  }
}

loadChatFromStorage()

export default function useChat() {
  const { sendMessageMutationAsync } = useChatMessageMutation()
  const { sendFeedbackMutationAsync } = useChatFeedbackMutation()
  const { chatContext } = useChatContext()

  const resetIdleTimer = () => {
    isIdle.value = false

    if (idleTimer) {
      clearTimeout(idleTimer)
    }

    idleTimer = setTimeout(() => {
      isIdle.value = true
      handleIdleFeedback()
    }, idleTimeMs)
  }

  const storeMessage = (message, author, extraData = {}) => {
    chatMessages.value.push({
      id: ++lastMessageId,
      text: message,
      author,
      timestamp: new Date(),
      ...extraData,
    })
    saveChatInStorage()
  }

  const addMessage = async (message, author) => {
    storeMessage(message, author)

    if (author === authors.USER) {
      resetIdleTimer()
      await sendUserMessage(message)
    }
  }

  const sendUserMessage = async message => {
    isAwaitingReply.value = true
    try {
      const { data } = await sendMessageMutationAsync({
        message,
        conversationId: conversationId.value,
        context: chatContext.value,
      })

      conversationId.value = data.id
      storeMessage(data.text, authors.BOT)
    } catch (error) {
      Sentry.withScope(scope => {
        scope.setTag('feature', 'chatbot')
        scope.setExtra('conversationId', conversationId.value)
        Sentry.captureException(error)
      })
    } finally {
      isAwaitingReply.value = false
    }
  }

  const handleIdleFeedback = () => {
    if (chatMessages.value.length < 2) {
      return
    }

    const hasPendingFeedback = chatMessages.value.some(m => m.author === authors.FEEDBACK && m.feedbackStatus === null)

    if (hasPendingFeedback) {
      return
    }

    const recentMessages = chatMessages.value.slice(-5)
    const hasRecentFeedback = recentMessages.some(m => m.author === authors.FEEDBACK)

    if (hasRecentFeedback) {
      return
    }

    addFeedbackRequest()
  }

  const addFeedbackRequest = () => {
    storeMessage(null, authors.FEEDBACK, {
      feedbackStatus: null,
      conversationId: conversationId.value,
      triggeredBy: 'idle',
    })
  }

  const submitFeedback = async (feedbackMessageId, status) => {
    const feedbackMessage = chatMessages.value.find(m => m.id === feedbackMessageId)
    if (!feedbackMessage) {
      return
    }
    try {
      feedbackMessage.feedbackStatus = status
      saveChatInStorage()
      await sendFeedbackMutationAsync({
        id: conversationId.value,
        status,
      })

      resetIdleTimer()
    } catch (error) {
      feedbackMessage.feedbackStatus = null
      saveChatInStorage()

      Sentry.withScope(scope => {
        scope.setTag('feature', 'chatbot-feedback')
        scope.setExtra('conversationId', conversationId.value)
        scope.setExtra('feedbackStatus', status)
        Sentry.captureException(error)
      })
    }
  }

  const clearChat = () => {
    chatMessages.value = []
    conversationId.value = null
    lastMessageId = 0
    sessionStorage.removeItem('chatSession')
    sessionStorage.removeItem('conversationId')

    if (idleTimer) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
    isIdle.value = false
  }

  resetIdleTimer()

  onUnmounted(() => {
    if (idleTimer) {
      clearTimeout(idleTimer)
    }
  })

  return {
    isChatOpen,
    isAwaitingReply,
    chatMessages,
    conversationId,
    isIdle,
    addMessage,
    clearChat,
    submitFeedback,
    resetIdleTimer,
  }
}
